import { z } from "zod";

// What a remote agent costs (ADR-023 §13). The Remote agent add-on gives one machine and a set
// number of awake hours each calendar month; extra hours come in packs, bought like a credit pack,
// and they carry over. A turn on our model draws the organization's AI credits; on the member's
// own Claude or Codex login it costs nothing here. Out of hours or AI credits, the next wake or
// turn is refused before it starts, so nothing is charged past what was paid (D551).
//
// Every price below is PROVISIONAL: Sergio sets the real ones before launch (D641). They live in
// code like every other price (ADR-019 §3) and show on /ops/providers, Payments, Prices.

/** Provisional, Sergio sets before launch (D641): euro cents a month, and the awake hours it
 * includes each calendar month (UTC). */
export const REMOTE_AGENT_ADDON = { monthlyCents: 1_900, hoursIncluded: 60 } as const;

/** Provisional, Sergio sets before launch (D641): extra awake hours, in euro cents. */
export const AWAKE_HOUR_PACKS: ReadonlyArray<{
  readonly hours: number;
  readonly cents: number;
  readonly recommended?: boolean;
}> = [
  { hours: 10, cents: 500 },
  { hours: 30, cents: 1_200, recommended: true },
  { hours: 100, cents: 3_500 },
];

/** Provisional, Sergio sets before launch (D641): the AI credits a turn on our model draws per
 * million tokens it read and wrote. */
export const MODEL_TURN_PRICES = { inputPerMillion: 500, outputPerMillion: 2_500 } as const;

/** Provisional, Sergio sets before launch (D675): the AI credits per million tokens a turn reads
 * again from the model's prompt cache, a tenth of a fresh read, as the model's own price has it. */
export const MODEL_CACHED_INPUT_PER_MILLION = 50;

const minutes = z.number().int().nonnegative();

/** The organization's awake time (D642): this month's included hours, what is used of them, and
 * what packs left. Spending takes the included minutes first, then the packs'. */
export const awakeHoursSchema = z.object({
  /** The Remote agent add-on is bought: without it there is no machine to wake. */
  hasAddon: z.boolean(),
  /** The calendar month (UTC) the included hours are for, `YYYY-MM`. */
  month: z.string(),
  includedMinutes: minutes,
  /** Of the included minutes, used this month. */
  usedMinutes: minutes,
  /** Bought in packs and not spent yet; they never expire. */
  packMinutes: minutes,
  /** What a wake may still use: the included minutes left plus the packs'. */
  remainingMinutes: minutes,
});
export type AwakeHours = z.infer<typeof awakeHoursSchema>;

/** One stretch a computer was awake. The same computer and stretch is charged once, so the runtime
 * may report it again after a crash or a retry (D643). */
export const awakeWindowSchema = z
  .object({
    computerId: z.string().min(1).max(200),
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine((window) => Date.parse(window.from) < Date.parse(window.to), "A window ends after it starts.");
export type AwakeWindow = z.infer<typeof awakeWindowSchema>;

export const spendAwakeMinutesInputSchema = z.object({
  /** Whole minutes, at most the window's own length rounded up. */
  minutes: z.number().int().positive(),
  source: awakeWindowSchema,
});
export type SpendAwakeMinutesInput = z.infer<typeof spendAwakeMinutesInputSchema>;

/** Whose model a turn runs on: ours draws AI credits; the member's own login costs nothing. */
export const turnModelSchema = z.enum(["ours", "own-login"]);
export type TurnModel = z.infer<typeof turnModelSchema>;

export const modelTurnUsageSchema = z.object({
  /** Read fresh, prompt cache writes included. */
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  /** Read again from the prompt cache, priced at `MODEL_CACHED_INPUT_PER_MILLION` (D675). */
  cachedInputTokens: z.number().int().nonnegative().default(0),
});
export type ModelTurnUsage = z.input<typeof modelTurnUsageSchema>;

export const modelTurnInputSchema = z.object({
  agentId: z.string().min(1).max(200),
  turnId: z.string().min(1).max(200),
  /** One model call of the turn (D675): each is charged once, as it ends, so a turn's balance
   * runs down call by call. Absent, the whole turn is charged once (D644). */
  callId: z.string().min(1).max(200).optional(),
  /** The agent's name as it is now, so the credit history names it (D676). */
  agentName: z.string().min(1).max(200).optional(),
  model: turnModelSchema,
  usage: modelTurnUsageSchema,
});
export type ModelTurnInput = z.input<typeof modelTurnInputSchema>;

/** The AI credits each turn of our model drew so far, for the agent's page (D676). */
export const modelTurnCreditsInputSchema = z.object({
  turnIds: z.array(z.string().min(1).max(200)).max(100),
});
export type ModelTurnCreditsInput = z.infer<typeof modelTurnCreditsInputSchema>;

export const modelTurnCreditsSchema = z.object({
  turns: z.array(z.object({ turnId: z.string(), credits: z.number().int().nonnegative() })),
});
export type ModelTurnCredits = z.infer<typeof modelTurnCreditsSchema>;

/** Development and tests only (refused in production, like `simulate`): set this month's used
 * minutes, to see a nearly or fully used month without a machine running. */
export const simulateAwakeInputSchema = z.object({
  usedMinutes: z
    .number()
    .int()
    .nonnegative()
    .max(31 * 24 * 60),
});
export type SimulateAwakeInput = z.infer<typeof simulateAwakeInputSchema>;

/** The remote agent's prices as the back office lists them; `provisional` until Sergio sets them. */
export const remoteAgentPricesSchema = z.object({
  provisional: z.boolean(),
  monthlyCents: z.number().int(),
  hoursIncluded: z.number().int(),
  hourPacks: z.array(
    z.object({ hours: z.number().int(), cents: z.number().int(), recommended: z.boolean() }),
  ),
  modelTurn: z.object({ inputPerMillion: z.number().int(), outputPerMillion: z.number().int() }),
});
export type RemoteAgentPrices = z.infer<typeof remoteAgentPricesSchema>;
